import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export const getDefaults = () => api.get('/defaults').then(r => r.data);
export const calculateAppraisal = (inputs) => api.post('/calculate', inputs).then(r => r.data);
export const createScenario = (data) => api.post('/scenarios', data).then(r => r.data);
export const listScenarios = () => api.get('/scenarios').then(r => r.data);
export const getScenario = (id) => api.get(`/scenarios/${id}`).then(r => r.data);
export const updateScenario = (id, data) => api.put(`/scenarios/${id}`, data).then(r => r.data);
export const deleteScenario = (id) => api.delete(`/scenarios/${id}`).then(r => r.data);
export const compareScenarios = (ids) => api.post('/scenarios/compare', { scenario_ids: ids }).then(r => r.data);
export const compareCashflows = (ids) => api.post('/scenarios/cashflow-compare', { scenario_ids: ids }).then(r => r.data);
// Polling helper: starts an AI task, then polls for result
const pollAiTask = async (startFn) => {
  const startRes = await startFn();
  const taskId = startRes.data?.task_id || startRes.data;
  const id = typeof taskId === 'string' ? taskId : taskId?.task_id;
  if (!id) throw new Error('No task_id returned');
  // Poll every 3 seconds for up to 5 minutes
  for (let i = 0; i < 100; i++) {
    await new Promise(r => setTimeout(r, 3000));
    const poll = await api.get(`/ai/task/${id}`, { timeout: 10000 });
    if (poll.data.status === 'done') return { success: true, data: poll.data.data };
    if (poll.data.status === 'error') throw new Error(poll.data.error || 'AI analysis failed');
  }
  throw new Error('Analysis timed out — please try again');
};

export const getLocationIntelligence = (postcode) => pollAiTask(() => api.post('/ai/location', { postcode }));
export const sendChatMessage = (session_id, message) => api.post('/ai/chat', { session_id, message }, { timeout: 180000 }).then(r => r.data);
export const getChatHistory = (session_id) => api.get(`/ai/chat/${session_id}`).then(r => r.data);
export const runSensitivity = (inputs) => api.post('/sensitivity', { inputs }).then(r => r.data);
export const autoGenerateScenarios = (postcode, project_name = '') => api.post('/ai/auto-scenarios', { postcode, project_name }).then(r => r.data);
export const getPlanningPolicy = (postcode, address = '') => pollAiTask(() => api.post('/ai/planning-policy', { postcode, address }));
export const getPlanningCilS106 = (postcode, address = '') => pollAiTask(() => api.post('/ai/planning-cil-s106', { postcode, address }));
export const getPlanningHousingNeeds = (postcode, address = '') => pollAiTask(() => api.post('/ai/planning-housing-needs', { postcode, address }));
export const exportPlanningReport = (format, policy, cil, housing) => api.post('/ai/planning-report', { format, policy, cil, housing }, { responseType: 'blob', timeout: 60000 }).then(r => r.data);
export const getComparables = (postcode) => pollAiTask(() => api.post('/ai/comparables', { postcode }));
export const getRiskAssessment = (scenario_id, postcode) => api.post('/ai/risk-assessment', { scenario_id, postcode }).then(r => r.data);
export const shareScenario = (scenarioId, email) => api.post(`/scenarios/${scenarioId}/share`, { email }).then(r => r.data);
export const unshareScenario = (scenarioId, email) => api.delete(`/scenarios/${scenarioId}/share/${encodeURIComponent(email)}`).then(r => r.data);
export const createShareLink = (scenarioId) => api.post(`/scenarios/${scenarioId}/share-link`).then(r => r.data);
export const deleteShareLink = (scenarioId) => api.delete(`/scenarios/${scenarioId}/share-link`).then(r => r.data);
export const getSharedScenario = (shareToken) => api.get(`/public/shared/${shareToken}`).then(r => r.data);
export const listTemplates = () => api.get('/templates').then(r => r.data);
export const getTemplate = (id) => api.get(`/templates/${id}`).then(r => r.data);

export const downloadReport = async (scenarioId, includeSensitivity = true) => {
  const response = await api.get(`/scenarios/${scenarioId}/report`, {
    params: { include_sensitivity: includeSensitivity },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `appraisal-report-${scenarioId.slice(0, 8)}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getCashflow = (scenarioId) => api.get(`/scenarios/${scenarioId}/cashflow`).then(r => r.data);

export const downloadCashflowPdf = async (scenarioId) => {
  const response = await api.get(`/scenarios/${scenarioId}/cashflow-pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `cashflow-report-${scenarioId.slice(0, 8)}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadExcel = async (scenarioId) => {
  const response = await api.get(`/scenarios/${scenarioId}/excel`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `appraisal-${scenarioId.slice(0, 8)}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const fmt = (val) => {
  if (val === null || val === undefined) return '-';
  const num = Number(val);
  if (isNaN(num)) return val;
  if (Math.abs(num) >= 1e6) return `\u00A3${(num / 1e6).toFixed(2)}m`;
  if (Math.abs(num) >= 1e3) return `\u00A3${num.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  return `\u00A3${num.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};

export const fmtFull = (val) => {
  if (val === null || val === undefined) return '-';
  return `\u00A3${Number(val).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};

export const fmtPct = (val) => {
  if (val === null || val === undefined) return '-';
  return `${(Number(val) * 100).toFixed(2)}%`;
};

export const fmtNum = (val) => {
  if (val === null || val === undefined) return '-';
  return Number(val).toLocaleString('en-GB', { maximumFractionDigits: 0 });
};

export default api;
